import { supabase } from './supabase'

// ─── FOLLOWS ─────────────────────────────────────────────────────────────────

export const followUser = async (followerId, followingId) => {
  const { error } = await supabase
    .from('follows')
    .insert({ follower_id: followerId, following_id: followingId })
  if (error) throw error

  // Create notification for the followed user
  await supabase.from('notifications').insert({
    user_id: followingId,
    type: 'new_follower',
    title: 'New follower',
    body: 'Someone started following you on Nakama!',
    url: '/community',
  })
}

export const unfollowUser = async (followerId, followingId) => {
  const { error } = await supabase
    .from('follows')
    .delete()
    .match({ follower_id: followerId, following_id: followingId })
  if (error) throw error
}

export const isFollowing = async (followerId, followingId) => {
  const { data } = await supabase
    .from('follows')
    .select('id')
    .match({ follower_id: followerId, following_id: followingId })
    .single()
  return !!data
}

export const getFollowers = async (userId) => {
  const { data, error } = await supabase
    .from('follows')
    .select('follower_id, profiles!follower_id(id, username, avatar_url)')
    .eq('following_id', userId)
  if (error) throw error
  return data.map(d => d.profiles)
}

export const getFollowing = async (userId) => {
  const { data, error } = await supabase
    .from('follows')
    .select('following_id, profiles!following_id(id, username, avatar_url)')
    .eq('follower_id', userId)
  if (error) throw error
  return data.map(d => d.profiles)
}

export const getFollowCounts = async (userId) => {
  const [{ count: followers }, { count: following }] = await Promise.all([
    supabase.from('follows').select('*', { count: 'exact', head: true }).eq('following_id', userId),
    supabase.from('follows').select('*', { count: 'exact', head: true }).eq('follower_id', userId),
  ])
  return { followers: followers || 0, following: following || 0 }
}

// ─── ACTIVITY FEED ───────────────────────────────────────────────────────────

export const logActivity = async (userId, action, content) => {
  const { error } = await supabase.from('activity_feed').insert({
    user_id: userId,
    action,
    content_id: content.id,
    content_type: content.type,
    content_title: content.title,
    content_cover: content.cover || null,
    rating: content.rating || null,
  })
  // Activity log failure is non-fatal — feed may be incomplete but app continues
}

export const getSocialFeed = async (userId, limit = 40) => {
  // Get list of followed user IDs first
  const { data: followed } = await supabase
    .from('follows')
    .select('following_id')
    .eq('follower_id', userId)

  const followedIds = (followed || []).map(f => f.following_id)
  // Include own activity + followed users
  const userIds = [userId, ...followedIds]

  const { data, error } = await supabase
    .from('activity_feed')
    .select('*, profiles(username, avatar_url)')
    .in('user_id', userIds)
    .order('created_at', { ascending: false })
    .limit(limit)
  if (error) throw error
  return (data || []).map(item => ({
    ...item,
    username: item.profiles?.username || item.username,
  }))
}

export const getPublicFeed = async (limit = 30) => {
  // For logged-out users: most recent activity across all users
  const { data, error } = await supabase
    .from('activity_feed')
    .select('*, profiles(username, avatar_url)')
    .in('action', ['started_reading', 'started_watching', 'completed_manga', 'completed_anime', 'rated_manga', 'rated_anime'])
    .order('created_at', { ascending: false })
    .limit(limit)
  if (error) throw error
  return data || []
}

export const getUserActivity = async (userId, limit = 20) => {
  const { data, error } = await supabase
    .from('activity_feed')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .limit(limit)
  if (error) throw error
  return data || []
}

// ─── TRENDING ────────────────────────────────────────────────────────────────

export const getTrendingContent = async (limit = 10) => {
  const { data, error } = await supabase.rpc('get_trending_content', { lim: limit })
  if (error) throw error
  return data || []
}

// ─── NOTIFICATIONS ───────────────────────────────────────────────────────────

export const getNotifications = async (userId, limit = 30) => {
  const { data, error } = await supabase
    .from('notifications')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .limit(limit)
  if (error) throw error
  return data || []
}

export const markNotificationRead = async (notifId) => {
  await supabase.from('notifications').update({ read: true }).eq('id', notifId)
}

export const markAllNotificationsRead = async (userId) => {
  await supabase.from('notifications').update({ read: true }).eq('user_id', userId).eq('read', false)
}

export const getUnreadCount = async (userId) => {
  const { count } = await supabase
    .from('notifications')
    .select('*', { count: 'exact', head: true })
    .eq('user_id', userId)
    .eq('read', false)
  return count || 0
}

// Create a chapter drop notification for a user
export const createChapterNotification = async (userId, mangaTitle, chapterNum, mangaId, chapterId) => {
  await supabase.from('notifications').insert({
    user_id: userId,
    type: 'new_chapter',
    title: `New chapter — ${mangaTitle}`,
    body: `Chapter ${chapterNum} is now available`,
    url: `/read/${mangaId}/${chapterId}`,
  })
}

// ─── DIGEST PREFERENCES ──────────────────────────────────────────────────────

export const getDigestPrefs = async (userId) => {
  const { data } = await supabase
    .from('digest_preferences')
    .select('*')
    .eq('user_id', userId)
    .maybeSingle()
  return data
}

export const upsertDigestPrefs = async (userId, prefs) => {
  const { data, error } = await supabase
    .from('digest_preferences')
    .upsert({ user_id: userId, ...prefs }, { onConflict: 'user_id' })
    .select()
    .single()
  if (error) throw error
  return data
}

// ─── PUSH NOTIFICATIONS (PWA) ────────────────────────────────────────────────

export const savePushSubscription = async (userId, subscription) => {
  const { endpoint, keys: { p256dh, auth } } = subscription.toJSON()
  const { error } = await supabase
    .from('push_subscriptions')
    .upsert({ user_id: userId, endpoint, p256dh, auth }, { onConflict: 'user_id,endpoint' })
  if (error) throw error
}

export const removePushSubscription = async (userId, endpoint) => {
  await supabase
    .from('push_subscriptions')
    .delete()
    .match({ user_id: userId, endpoint })
}

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64  = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const rawData = window.atob(base64)
  return Uint8Array.from([...rawData].map(c => c.charCodeAt(0)))
}

// Request push permission and subscribe
export const requestPushPermission = async (userId) => {
  if (!('Notification' in window) || !('serviceWorker' in navigator)) {
    return { success: false, reason: 'not_supported' }
  }

  const permission = await Notification.requestPermission()
  if (permission !== 'granted') {
    return { success: false, reason: 'denied' }
  }

  try {
    const reg    = await navigator.serviceWorker.ready
    const vapid  = import.meta.env.VITE_VAPID_PUBLIC_KEY?.trim()
    const existing = await reg.pushManager.getSubscription()
    const sub = existing || await reg.pushManager.subscribe({
      userVisibleOnly: true,
      ...(vapid ? { applicationServerKey: urlBase64ToUint8Array(vapid) } : {}),
    }).catch(() => null)

    if (sub) await savePushSubscription(userId, sub)
    // Clear dismissed flag so the banner won't re-appear after manual re-enable
    localStorage.removeItem('nakama-push-dismissed')
    return { success: true }
  } catch (e) {
    return { success: false, reason: 'error' }
  }
}
